import React, { useState, useEffect } from "react";
import T from "prop-types";
import classNames from "classnames";
import { Col, Row } from "react-bootstrap";
import { connect } from "react-redux";
import api from "APP_ROOT/api";
import { push } from "react-router-redux";
import { Map } from "immutable";
import { useScrollToOnMount } from "APP_ROOT/hooks";
import { loadRecord } from "MODULES/shared/decorators";
import { show as getPart, subscribe } from "MODULES/parts/actions";
import {
  Attachments,
  AuditFields,
  PartStatsDetail,
  DashedSeparator,
  EditButton,
  Notes,
  RecordPager,
  Authorized,
  ClipboardCopy,
  StatusTags,
  StatusTag
} from "MODULES/shared/components";
import resourceTypes from "MODULES/shared/types/resourceTypes";
import SellableQty from "MODULES/parts/components/shared/SellableQty";
import {
  WAREHOUSE,
  SALES,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  WAREHOUSE_EXPERT,
  WAREHOUSE_MANAGER,
  CAN_ADD_UPDATE_PARTS,
  CAN_MERGE_PART_NUMBERS
} from "MODULES/users/constants";
import {
  ATTACHMENT_TYPES,
  DESCRIPTION,
  PART_NUMBER,
  MILITARY,
  SELLABLE_QUANTITIES,
  TIER_TYPE
} from "MODULES/parts/constants";
import { getPartNumberColorClassNameByTierType } from "MODULES/shared/helpers";
import MergePartModal from "MODULES/parts/components/Detail/MergePartModal/MergePartModal";
import { ID, UUID } from "APP_ROOT/constants";
import Catalog from "MODULES/parts/components/Detail/Catalog";
import RepairQuotes from "MODULES/parts/components/Detail/RepairQuotes/RepairQuotes";
import RepairOrders from "MODULES/parts/components/Detail/RepairOrders";
import LotLineItems from "MODULES/parts/components/Detail/LotLineItems/LotLineItems";
import ManagedRepairOrders from "MODULES/parts/components/Detail/ManagedRepairOrders/ManagedRepairOrders";
import SendRfqNestedForm from "MODULES/parts/components/Detail/RepairQuotes/Send/SendRfqNestedForm";
import Pin from "MODULES/parts/components/Detail/Pin";
import EditPart from "../EditPart";
import Applications from "./Applications";
import Alternates from "./Alternates";
import Inventory from "./Inventory/Inventories";
import PurchaseOrders from "./PurchaseOrders/PurchaseOrders";
import SalesOrders from "./SalesOrders";
import IncomingQuotes from "./IncomingQuotes";
import OutgoingQuotes from "./OutgoingQuotes/OutgoingQuotes";
import CreateIncomingQuote from "./IncomingQuotes/Create/Create";
import CreateOutgoingQuote from "./OutgoingQuotes/Create";
import CreatePurchaseOrder from "./PurchaseOrders/Create/Create";
import CreateRepairQuote from "./RepairQuotes/Create/Create";
import UpdateRepairQuote from "./RepairQuotes/Update/Update";
import CreateManagedRepair from "./ManagedRepairOrders/CreateManagedRepair/CreateManagedRepair";
import Table from "./Table";
import EditDimensions from "../EditPart/EditDimensions";
import WeightAndDims from "./WeightAndDims";
import AllParts from "./AllParts/AllParts";

function PartLabel({ part }) {
  const tierType = part.get(TIER_TYPE);

  const classes = classNames(
    "header-content",
    getPartNumberColorClassNameByTierType(tierType)
  );

  return <span className={classes}>{part.get(PART_NUMBER)}</span>;
}

PartLabel.propTypes = {
  part: T.instanceOf(Map)
};

export function Details({ part, subscribeToPart }) {
  const [editPartModalOpen, setEditPartModalOpen] = useState(false);
  const [editDimModalOpen, setEditDimModalOpen] = useState(false);
  const [mergePartModalOpen, setMergePartModalOpen] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [activeRepairQuote, setActiveRepairQuote] = useState();
  const stats = usePartStats(part.get(ID));
  usePartSubscription(part.get(ID), subscribeToPart);
  const closeActiveForm = () => setActiveForm(null);
  const activateForm = formId => setActiveForm(formId);

  const militaryPart = part.get(MILITARY);
  const hasPmaAlternate = () => {
    const alternates = part.get("alternates").toJS();
    return alternates.some(alternate => alternate.pma);
  };

  return (
    <div>
      <div className="page-header page-header-bar">
        <h1>
          <div className="with-padding">
            <RecordPager baseUrl="/parts" record={part} />
            <PartLabel part={part} />
            <ClipboardCopy name={part.get(PART_NUMBER)} type="PN" />
            <span className="small text-muted">{part.get(DESCRIPTION)}</span>
            <Pin partId={part.get(ID)} />

            {Number(stats.on_order) > 0 && (
              <span className="pull-right">On Order: {stats.on_order}</span>
            )}
            <SellableQty quantities={part.get(SELLABLE_QUANTITIES)} />
            {militaryPart && (
              <StatusTags>
                <StatusTag
                  display="military"
                  title="Military"
                  icon="fighter-jet"
                />
              </StatusTags>
            )}
            {hasPmaAlternate() && (
              <StatusTags>
                <StatusTag display="warning" title="PMA" icon="warning" />
              </StatusTags>
            )}
          </div>
        </h1>
      </div>
      <div className="row">
        <div className="col-md-3 parts-detail-panel-width">
          <div className="panel panel-default">
            <div className="panel-body">
              <Alternates />
              <h2 style={{ marginTop: 10 }}>
                Details
                <Authorized
                  roles={[SALES]}
                  permissions={[CAN_ADD_UPDATE_PARTS, CAN_MERGE_PART_NUMBERS]}
                  subroles={[
                    WAREHOUSE_LOGISTICS_DIRECTOR,
                    WAREHOUSE_EXPERT,
                    WAREHOUSE_MANAGER
                  ]}
                >
                  <EditButton handleClick={() => setEditPartModalOpen(true)} />
                  <EditPart
                    part={part.toJS()}
                    open={editPartModalOpen}
                    close={() => setEditPartModalOpen(false)}
                    toggleMergePartModal={setMergePartModalOpen}
                  />
                </Authorized>
              </h2>
              <Table part={part} />
              <Catalog part={part} />
              <hr />
              <h2 style={{ letterSpacing: "initial" }}>
                Weight & Dims
                <Authorized roles={[WAREHOUSE]} allowManagers>
                  <EditButton handleClick={() => setEditDimModalOpen(true)} />
                  <EditDimensions
                    part={part.toJS()}
                    open={editDimModalOpen}
                    close={() => setEditDimModalOpen(false)}
                  />
                </Authorized>
                <Authorized permissions={[CAN_MERGE_PART_NUMBERS]}>
                  <MergePartModal
                    partId={part.get(ID)}
                    close={() => setMergePartModalOpen(false)}
                    open={mergePartModalOpen}
                  />
                </Authorized>
              </h2>
              <WeightAndDims part={part} />
              <DashedSeparator />
              <Applications />
              <AuditFields record={part} multiLine />
            </div>
          </div>

          <div className="panel panel-default">
            <div className="panel-body">
              <h2 style={{ marginTop: 10 }}>Part List</h2>
              <AllParts partId={part.get(UUID)} />
            </div>
          </div>
        </div>
        <div className="col-md-9 parts-remaining-width">
          <PartStatsDetail part={part} stats={stats} />
          <Inventory />
          <Row>
            <Col lg={activeForm ? 6 : 12}>
              <Row>
                <Col lg={activeForm ? 12 : 6}>
                  <PurchaseOrders
                    openForm={() => activateForm("po")}
                    showNewButton={!activeForm}
                  />
                </Col>
                <Col lg={activeForm ? 12 : 6}>
                  <SalesOrders />
                </Col>
              </Row>
              <Row>
                <Col lg={activeForm ? 12 : 6}>
                  <IncomingQuotes
                    openForm={() => activateForm("iq")}
                    showNewButton={!activeForm}
                  />
                </Col>
                <Col lg={activeForm ? 12 : 6}>
                  <OutgoingQuotes
                    openForm={() => activateForm("oq")}
                    showNewButton={!activeForm}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={activeForm ? 12 : 6}>
                  <RepairQuotes
                    openForm={() => activateForm("rq")}
                    showNewButton={!activeForm}
                    setActiveRepairQuote={l => {
                      setActiveRepairQuote(l);
                      activateForm("editRq");
                    }}
                  />
                </Col>
                <Col lg={activeForm ? 12 : 6}>
                  <RepairOrders />
                </Col>
              </Row>
              <Row>
                <Col lg={activeForm ? 12 : 6}>
                  <LotLineItems />
                </Col>
                <Col lg={activeForm ? 12 : 6}>
                  <ManagedRepairOrders
                    openForm={() => activateForm("mro")}
                    showNewButton={!activeForm}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={activeForm ? 12 : 6}>
                  <Notes
                    resource={part}
                    resourceType={resourceTypes.part}
                    resourceKey="part"
                  />
                </Col>
                <Col lg={activeForm ? 12 : 6}>
                  <hr className="hr-dashed" />
                  <Attachments
                    resource={part}
                    resourceType={resourceTypes.part}
                    resourceKey="part"
                    attachmentTypes={ATTACHMENT_TYPES}
                    multiple
                  />
                </Col>
              </Row>
            </Col>
            {activeForm && (
              <ActiveForm>
                {activeForm === "po" && (
                  <CreatePurchaseOrder
                    close={closeActiveForm}
                    part={part.toJS()}
                  />
                )}
                {activeForm === "iq" && (
                  <CreateIncomingQuote close={closeActiveForm} />
                )}
                {activeForm === "oq" && (
                  <CreateOutgoingQuote
                    part={part.toJS()}
                    close={closeActiveForm}
                  />
                )}
                {activeForm === "rq" && (
                  <CreateRepairQuote
                    close={data => {
                      if (data.sendRfq) {
                        setActiveRepairQuote(data.repair_quote_line_item);
                        setActiveForm("sendRfq");
                      } else {
                        window.scrollTo(0, 0);
                        setActiveForm(null);
                      }
                    }}
                    part={part.toJS()}
                  />
                )}
                {activeForm === "editRq" && (
                  <UpdateRepairQuote
                    close={data => {
                      if (data.sendRfq) {
                        setActiveRepairQuote(data.repair_quote_line_item);
                        setActiveForm("sendRfq");
                      } else {
                        window.scrollTo(0, 0);
                        setActiveForm(null);
                      }
                    }}
                    lineItem={activeRepairQuote}
                  />
                )}
                {activeForm === "mro" && (
                  <CreateManagedRepair
                    part={part.toJS()}
                    close={closeActiveForm}
                  />
                )}
                {activeForm === "sendRfq" && (
                  <SendRfqNestedForm
                    close={closeActiveForm}
                    lineItem={activeRepairQuote}
                  />
                )}
              </ActiveForm>
            )}
          </Row>
        </div>
      </div>
    </div>
  );
}

Details.propTypes = {
  part: T.instanceOf(Map),
  subscribeToPart: T.func.isRequired
};

export default connect(
  state => ({
    part: state.partDetail.get("part")
  }),
  dispatch => ({
    subscribeToPart: part => dispatch(subscribe(part)),
    pushHistory: url => dispatch(push(url))
  })
)(
  loadRecord({
    reducer: "partDetail",
    name: "part",
    action: getPart
  })(Details)
);

function ActiveForm({ children }) {
  const scrollToRef = useScrollToOnMount();

  return (
    <Col lg={6} ref={scrollToRef}>
      {children}
    </Col>
  );
}

ActiveForm.propTypes = {
  children: T.oneOfType([T.node, T.arrayOf(T.node)])
};

// Custom hooks
function usePartSubscription(partId, subscribeTo) {
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!subscription) {
      setSubscription(subscribeTo(partId));
    }
    return function cleanup() {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  return subscription;
}

function usePartStats(partId) {
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.reports.part_stats.search(partId).then(res => setStats(res.data));
    return function cleanup() {};
  }, []);

  return stats;
}



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/Detail.jsx